// =============================================================================
// FILE: packages/renderer/src/components/r2/AnxietyControlPanel.tsx
// PURPOSE: R2 Anxiety Control — a protective, calming companion. On activation
//   R2 speaks a calm greeting and offers Yes / No. "Yes" reveals assistance
//   options (organize tasks, review held messages, calming audio, breathing
//   guide, prioritize day). Non-priority communications are held via the
//   Notification Intelligence Engine and surfaced with calm, non-interrupting
//   phrasing. Renders globally; visible only in Anxiety Control mode.
// =============================================================================

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import {
  Shield, X, Volume2, Loader2, Wind, ListTodo, Inbox, Music, Sparkles,
  CheckCircle2, MinusCircle, ShieldCheck,
} from 'lucide-react';
import { useExperienceMode } from '../../stores/experienceMode.store.js';
import { speak, stopSpeaking } from '../../lib/voice/voiceService.js';
import {
  getHeldMessages, releaseMessage, clearHeld, simulateIncoming, calmPhrasing,
  type ClassifiedMessage,
} from '../../lib/notifications/notificationIntelligence.js';

const GREETING = 'Anxiety Control Mode is active. I will help reduce unnecessary interruptions and keep your environment calm. Is there anything I can do?';
const QUIET = 'Understood. I will remain quietly available. You are in control. I will only notify you when something important requires your attention.';

type Stage = 'greeting' | 'assist' | 'quiet';
type Assist = 'tasks' | 'messages' | 'calm' | 'audio' | 'breathe' | 'prioritize' | null;

export function AnxietyControlPanel(): JSX.Element | null {
  const mode = useExperienceMode((s) => s.mode);
  const panelOpen = useExperienceMode((s) => s.panelOpen);
  const openPanel = useExperienceMode((s) => s.openPanel);
  const closePanel = useExperienceMode((s) => s.closePanel);
  const deactivate = useExperienceMode((s) => s.deactivate);

  const [stage, setStage] = useState<Stage>('greeting');
  const [assist, setAssist] = useState<Assist>(null);
  const [speaking, setSpeaking] = useState(false);
  const [held, setHeld] = useState<ClassifiedMessage[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const greetedRef = useRef(false);

  const say = useCallback(async (text: string) => {
    setSpeaking(true);
    await speak(text, 'r2-female');
    setSpeaking(false);
  }, []);

  // Speak the calm greeting once each time the panel is (re)activated.
  useEffect(() => {
    if (mode === 'anxiety' && panelOpen && !greetedRef.current) {
      greetedRef.current = true;
      setStage('greeting'); setAssist(null);
      setHeld(getHeldMessages());
      void say(GREETING);
    }
    if (mode !== 'anxiety') { greetedRef.current = false; stopSpeaking(); }
  }, [mode, panelOpen, say]);

  const onYes = useCallback(() => { stopSpeaking(); setStage('assist'); void say('How can I help?'); }, [say]);
  const onNo = useCallback(() => { stopSpeaking(); setStage('quiet'); void say(QUIET); }, [say]);

  const chooseAssist = useCallback((a: Assist) => {
    setAssist(a);
    const lines: Record<string, string> = {
      tasks: 'Let us organize your tasks. I will group them by priority so nothing feels overwhelming.',
      messages: 'Here are the messages I have safely held. Review them whenever you feel ready.',
      calm: 'Calming mode engaged. The interface is dimmed, sounds are quieted, and interruptions are held.',
      audio: 'Playing a gentle ambient tone. Let it soften the moment.',
      breathe: 'Let us breathe together. Follow the circle — in, hold, and out.',
      prioritize: 'Let us prioritize your day. Tell me your top three outcomes and I will help you focus on them.',
    };
    if (a) { const line = lines[a]; if (line) void say(line); }
    if (a === 'messages') setHeld(getHeldMessages());
    if (a === 'audio') playAmbientTone();
  }, [say]);

  const doSimulate = useCallback(() => {
    const m = simulateIncoming();
    setHeld(getHeldMessages());
    if (m.priority === 'held') setToast(calmPhrasing(m));
    else setToast(`Priority message from ${m.sender} was allowed through.`);
    setTimeout(() => setToast(null), 6000);
  }, []);

  const review = useCallback((m: ClassifiedMessage) => {
    setHeld(releaseMessage(m.id));
    setToast(`Reviewed: "${m.subject}" from ${m.sender}.`);
    setTimeout(() => setToast(null), 4000);
  }, []);

  if (mode !== 'anxiety') return null;

  // Minimised guardian launcher when the panel is closed but mode is active.
  if (!panelOpen) {
    return createPortal(
      <button
        type="button" data-testid="anxiety-guardian-fab" data-nosound onClick={openPanel}
        title="Anxiety Control is active — open R2 companion"
        style={{ position: 'fixed', left: 20, bottom: 20, zIndex: 9600, height: 52, width: 52, borderRadius: '50%',
          display: 'grid', placeItems: 'center', cursor: 'pointer', color: '#a7f3d0',
          background: 'rgba(6,20,18,0.94)', border: '1px solid rgba(52,211,153,0.5)',
          boxShadow: '0 8px 26px rgba(0,0,0,0.5), 0 0 22px rgba(52,211,153,0.22)' }}
      >
        <ShieldCheck style={{ width: 22, height: 22 }} />
      </button>,
      document.body,
    );
  }

  return createPortal(
    <div data-testid="anxiety-control-panel" style={{ position: 'fixed', inset: 0, zIndex: 10040, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(2,6,10,0.55)', backdropFilter: 'blur(4px)' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto', borderRadius: 18, background: 'linear-gradient(180deg, #0a1512, #0b1220)', border: '1px solid rgba(52,211,153,0.35)', boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid rgba(52,211,153,0.16)' }}>
          <Shield style={{ width: 18, height: 18, color: '#6ee7b7' }} />
          <div style={{ lineHeight: 1.2 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#eafff6' }}>Anxiety Control Mode</div>
            <div style={{ fontSize: 10.5, color: 'rgba(148,163,184,0.75)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Protective · calm environment</div>
          </div>
          {speaking && <Volume2 style={{ width: 15, height: 15, color: '#6ee7b7', marginLeft: 8 }} className="animate-pulse" />}
          <button type="button" data-testid="anxiety-minimise" data-nosound onClick={closePanel} title="Minimise" style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'rgba(148,163,184,0.6)', cursor: 'pointer' }}><X style={{ width: 16, height: 16 }} /></button>
        </div>

        <div style={{ padding: '18px 20px 22px' }}>
          {/* R2 calm message */}
          <div style={{ padding: '14px 16px', borderRadius: 12, background: 'rgba(16,32,28,0.7)', border: '1px solid rgba(52,211,153,0.22)', color: '#d1fae5', fontSize: 13.5, lineHeight: 1.6 }}>
            <span style={{ fontWeight: 700, color: '#6ee7b7' }}>R2:</span>{' '}
            {stage === 'quiet' ? QUIET : stage === 'assist' ? 'How can I help?' : GREETING}
          </div>

          {/* Greeting choices */}
          {stage === 'greeting' && (
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button type="button" data-testid="anxiety-yes" onClick={onYes}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', color: '#04110f', background: 'linear-gradient(135deg,#34d399,#10b981)', border: 'none' }}>
                Yes, help me
              </button>
              <button type="button" data-testid="anxiety-no" onClick={onNo}
                style={{ flex: 1, padding: '10px 12px', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', color: '#cbd5e1', background: 'rgba(30,41,59,0.7)', border: '1px solid rgba(148,163,184,0.3)' }}>
                No, I am okay
              </button>
            </div>
          )}

          {/* Assistance options */}
          {stage === 'assist' && (
            <>
              <div data-testid="anxiety-assist-options" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
                {[
                  { id: 'tasks', label: 'Organize my tasks', icon: ListTodo },
                  { id: 'messages', label: 'Review important messages', icon: Inbox },
                  { id: 'calm', label: 'Start calming mode', icon: Sparkles },
                  { id: 'audio', label: 'Play calming audio', icon: Music },
                  { id: 'breathe', label: 'Guide me through breathing', icon: Wind },
                  { id: 'prioritize', label: 'Help me prioritize my day', icon: ShieldCheck },
                ].map((o) => {
                  const Icon = o.icon; const active = assist === o.id;
                  return (
                    <button key={o.id} type="button" data-testid={`anxiety-assist-${o.id}`} onClick={() => chooseAssist(o.id as Assist)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 12px', borderRadius: 10, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                        color: active ? '#6ee7b7' : '#cbd5e1',
                        background: active ? 'rgba(52,211,153,0.14)' : 'rgba(15,23,42,0.6)',
                        border: `1px solid ${active ? 'rgba(52,211,153,0.5)' : 'rgba(148,163,184,0.2)'}` }}>
                      <Icon style={{ width: 15, height: 15, flexShrink: 0 }} /> {o.label}
                    </button>
                  );
                })}
              </div>

              {assist === 'breathe' && <BreathingGuide />}

              {assist === 'messages' && (
                <div data-testid="anxiety-held-messages" style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em', color: 'rgba(148,163,184,0.7)' }}>Safely held · {held.length}</span>
                    {held.length > 0 && (
                      <button type="button" data-nosound onClick={() => { clearHeld(); setHeld([]); }} style={{ fontSize: 11, color: 'rgba(148,163,184,0.7)', background: 'transparent', border: 'none', cursor: 'pointer' }}>Clear all</button>
                    )}
                  </div>
                  {held.length === 0 ? (
                    <p style={{ fontSize: 12.5, color: 'rgba(148,163,184,0.6)' }}>Nothing is waiting. Your space is calm.</p>
                  ) : held.map((m) => (
                    <div key={m.id} style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.18)', marginBottom: 8 }}>
                      <div style={{ fontSize: 12.5, color: '#e2e8f0', fontWeight: 600 }}>{m.subject}</div>
                      <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.7)', marginTop: 2 }}>{m.sender} · {m.reason}</div>
                      <div style={{ fontSize: 11.5, color: '#a7f3d0', marginTop: 6 }}>{calmPhrasing(m)}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        <button type="button" data-testid={`anxiety-review-${m.id}`} onClick={() => review(m)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, padding: '5px 10px', borderRadius: 8, cursor: 'pointer', color: '#04110f', background: '#34d399', border: 'none' }}><CheckCircle2 style={{ width: 13, height: 13 }} /> Yes — review it</button>
                        <button type="button" onClick={() => { setHeld(releaseMessage(m.id)); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, padding: '5px 10px', borderRadius: 8, cursor: 'pointer', color: '#cbd5e1', background: 'rgba(30,41,59,0.7)', border: '1px solid rgba(148,163,184,0.3)' }}><MinusCircle style={{ width: 13, height: 13 }} /> No — I am okay</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Footer controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
            <button type="button" data-testid="anxiety-simulate" data-nosound onClick={doSimulate}
              style={{ fontSize: 11.5, fontWeight: 600, padding: '7px 12px', borderRadius: 8, cursor: 'pointer', color: '#93c5fd', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
              Simulate incoming message
            </button>
            <button type="button" data-testid="anxiety-exit" data-nosound onClick={() => { stopSpeaking(); deactivate(); }}
              style={{ marginLeft: 'auto', fontSize: 11.5, fontWeight: 700, padding: '7px 12px', borderRadius: 8, cursor: 'pointer', color: '#fca5a5', background: 'rgba(244,63,94,0.1)', border: '1px solid rgba(244,63,94,0.35)' }}>
              Exit Anxiety Control
            </button>
          </div>

          {toast && (
            <div data-testid="anxiety-toast" style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'rgba(16,32,28,0.8)', border: '1px solid rgba(52,211,153,0.3)', color: '#d1fae5', fontSize: 12.5, lineHeight: 1.5 }}>
              {toast}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// Simple 4-7-8 breathing guide with an expanding circle.
function BreathingGuide(): JSX.Element {
  const phases = [
    { label: 'Breathe in', ms: 4000, scale: 1.5 },
    { label: 'Hold', ms: 7000, scale: 1.5 },
    { label: 'Breathe out', ms: 8000, scale: 0.85 },
  ];
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setI((p) => (p + 1) % phases.length), phases[i]!.ms);
    return () => clearTimeout(t);
  }, [i]);
  const p = phases[i]!;
  return (
    <div data-testid="anxiety-breathing" style={{ marginTop: 16, display: 'grid', placeItems: 'center', padding: '10px 0 4px' }}>
      <div style={{ width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(52,211,153,0.25), rgba(52,211,153,0.05))', border: '1px solid rgba(52,211,153,0.5)', transform: `scale(${p.scale})`, transition: `transform ${p.ms}ms ease-in-out`, display: 'grid', placeItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#6ee7b7' }}>{p.label}</span>
      </div>
      <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.7)', marginTop: 18 }}>Follow the rhythm — 4 in · 7 hold · 8 out</div>
    </div>
  );
}

// Gentle ambient tone (WebAudio) — soft, short, non-startling.
function playAmbientTone(): void {
  try {
    const Ctx = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext });
    const AC = Ctx.AudioContext || Ctx.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine'; o.frequency.value = 174; // low, calming
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 1.2);
    g.gain.linearRampToValueAtTime(0, ctx.currentTime + 6);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 6.2);
    o.onended = () => { try { void ctx.close(); } catch { /* */ } };
  } catch { /* audio optional */ }
}
